import { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Logo from '../components/Logo';
import checkIcon from '../../../assets/check-icon.svg';
import { signupContentStyles } from '../components/styles';
import MiddleContent from '../components/MiddleContent';
import { useNavigate } from 'react-router-dom';
import { useRouteConfig } from '../utils/useRouteConfig';
import { PATH } from '../../../routes';
import { pushEvent } from '../../../utils/clevertapUtils';
import {
    PAGE_WELCOME,
    START_BUTTON_CLICK,
} from '../../../utils/clevertapEvents';
import withWidth, {
    isWidthUp,
    WithWidthProps,
} from '@material-ui/core/withWidth';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { getOfficerTitles } from '../../../store/signup/thunks';
import { AppState } from 'store';
import { Features } from 'store/featureFlags/types';
import { useFullStoryIdentity } from '../utils/useFullStoryIdentity';
import { DesignTokens, GivelifyButton, GivelifyLabel } from '@givelify/ui';

const breakPoint: 'md' | 'xs' | 'sm' | 'lg' | 'xl' = 'sm';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        welcomeStyles: {
            textAlign: 'center',
            paddingBottom: 55,
            '& .heading': {
                marginBottom: theme.spacing(4),
                fontWeight: 700,
                color: DesignTokens.color.textPrimary,
                [theme.breakpoints.down('xs')]: {
                    marginTop: 4,
                    marginBottom: theme.spacing(2),
                },
            },
            '& .button': {
                minWidth: '207px',
                marginBottom: theme.spacing(6),
                minHeight: '38px',
            },
            '& .sub-heading': {
                marginBottom: theme.spacing(5),
                [theme.breakpoints.down('xs')]: {
                    maxWidth: 263,
                    margin: '0 auto',
                    marginBottom: theme.spacing(3),
                },
            },
            '& .points-container': {
                border: '0.5px solid rgba(133, 145, 159, 0.6)',
                borderRadius: '8px',
                maxWidth: 710,
                marginRight: 'auto',
                marginLeft: 'auto',
                textAlign: 'left',
                paddingLeft: 58,
                paddingTop: theme.spacing(5),
                paddingBottom: theme.spacing(3),
                paddingRight: theme.spacing(3),
                [theme.breakpoints.down('sm')]: {
                    maxWidth: 628,
                    paddingTop: theme.spacing(3),
                    paddingBottom: 0,
                    paddingLeft: theme.spacing(4),
                },
                [theme.breakpoints.down('xs')]: {
                    paddingLeft: theme.spacing(3),
                },
            },
            [theme.breakpoints.down('xs')]: {
                '& .heading': {
                    fontSize: 18,
                },
                '& .sub-heading': {
                    fontSize: 14,
                },
            },
        },
        mobileButtonContainerStyles: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            maxHeight: 74,
            padding: theme.spacing(3),
            zIndex: 99,
            backgroundColor: '#fff',
            boxShadow: '0px 0px 8px rgba(58, 76, 99, 0.16)',
            '& button': {
                minHeight: '38px',
            },
            '& .MuiButtonBase-root': {
                marginBottom: 0,
                height: 42,
                width: '100%',
            },
        },
        bulletStyles: {
            display: 'flex',
            alignItems: 'baseline',
            marginBottom: theme.spacing(3),
            '& div': {
                flex: '1 1 auto',
                lineHeight: '28px',
            },
            '& img': {
                marginRight: theme.spacing(3),
                [theme.breakpoints.down('xs')]: {
                    marginRight: 10,
                },
            },
        },
    }),
);

const BulletPoint: React.FC<{ text: string }> = ({ text }) => {
    const { bulletStyles } = useStyles();
    return (
        <div className={bulletStyles}>
            <img src={checkIcon} title="Check" alt="Check" />
            <GivelifyLabel wrap variant="body1" text={text} />
        </div>
    );
};

const Welcome: React.FC<WithWidthProps> = ({ width }) => {
    width = width || 'xs';
    const push = useNavigate();
    const { next } = useRouteConfig(PATH.SIGNUP.WELCOME);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { fingerprint, fullStoryEnabled } = useSelector(
        (state: AppState) => ({
            fingerprint: state.Signup.fingerprint,
            fullStoryEnabled: state.FeatureFlagging.features[Features.FULLSTORY],
        }),
    );
    const { welcomeStyles, mobileButtonContainerStyles } = useStyles();
    const { logo } = signupContentStyles();
    const onClick = () => {
        push(next);
        pushEvent(START_BUTTON_CLICK);
    };
    useEffect(() => {
        pushEvent(PAGE_WELCOME);
        dispatch(getOfficerTitles());
    }, [dispatch]);
    useFullStoryIdentity({
        enabled: fullStoryEnabled,
        fingerprint,
    });
    const { list, startLabel, heading, subHeading } = useMemo(
        () => ({
            heading: t('signup.welcome.heading'),
            subHeading: t('signup.welcome.subHeading'),
            startLabel: t('labels.start'),
            list: [
                t('signup.welcome.bulletPoints.first'),
                t('signup.welcome.bulletPoints.second'),
                t('signup.welcome.bulletPoints.third'),
                t('signup.welcome.bulletPoints.fourth'),
            ],
        }),
        [t],
    );
    const isBiggerScreen = isWidthUp(breakPoint, width);
    return (
        <>
            <Header
                hideLogo={isBiggerScreen ? true : undefined}
                backUrl={undefined}
                showCloseButton={true}
            />
            <MiddleContent heading={undefined}>
                <div className={welcomeStyles}>
                    {isBiggerScreen && <Logo className={logo} />}
                    <GivelifyLabel
                        variant="h2legacy"
                        text={heading}
                        className="heading"
                    />
                    <GivelifyLabel
                        wrap
                        color="#73717F"
                        variant="body1"
                        text={subHeading}
                        className="sub-heading"
                    />
                    {isBiggerScreen && (
                        <GivelifyButton
                            size="small"
                            variant="primary"
                            text={startLabel}
                            onClick={onClick}
                            className="button"
                        />
                    )}
                    <div className="points-container">
                        {list.map((l, i) => (
                            <BulletPoint key={i} text={l} />
                        ))}
                    </div>
                </div>
                {!isBiggerScreen && (
                    <div className={mobileButtonContainerStyles}>
                        <GivelifyButton
                            fullWidth
                            size="large"
                            variant="primary"
                            text={startLabel}
                            onClick={onClick}
                        />
                    </div>
                )}
            </MiddleContent>
        </>
    );
};

export default withWidth()(Welcome);
